import { motion } from "framer-motion";

/**
 * Punti normalizzati sul viewBox 0..100 dove l'origine radiale è il Duomo (46,58).
 * Le distanze rispecchiano grossolanamente le zone reali di Milano rispetto al centro.
 */
const POINTS = [
  { name: "Centrale", x: 52, y: 38, active: true },
  { name: "Repubblica", x: 48, y: 46, active: true },
  { name: "Maciachini", x: 42, y: 28, active: true },
  { name: "Bicocca", x: 68, y: 22, active: true },
  { name: "Porta Venezia", x: 58, y: 50, active: true },
  { name: "Loreto", x: 62, y: 42, active: true },
  { name: "Bignami", x: 48, y: 18, active: true },
  { name: "Sondrio", x: 55, y: 30, active: true },
  { name: "Duomo", x: 46, y: 58, active: false },
  { name: "Cinque Giornate", x: 56, y: 62, active: false },
  { name: "Gioia", x: 50, y: 36, active: false },
  { name: "Monumentale", x: 38, y: 42, active: false },
  { name: "Rho Fiera", x: 14, y: 50, active: false },
];

export default function MilanMap() {
  return (
    <section
      data-testid="milan-map"
      className="relative py-24 md:py-36 px-6 md:px-12 max-w-[1440px] mx-auto"
    >
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-white/50">
            <span className="h-px w-8 bg-[#C48F56]" />
            Perché Milano
          </div>
          <h2 className="mt-5 font-display text-5xl md:text-6xl tracking-tightest leading-[0.95]">
            Una mappa di <span className="text-[#C48F56]">occasioni.</span>
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed">
            Dinamica, internazionale, in costante evoluzione: Milano è uno dei
            principali poli europei per sviluppo e attrattività. È qui che
            concentriamo i nostri investimenti.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-5">
            <div>
              <div className="font-display text-4xl">15</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">
                Hotel attivi
              </div>
            </div>
            <div>
              <div className="font-display text-4xl text-[#C48F56]">5</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1">
                In costruzione
              </div>
            </div>
          </div>
          <div className="mt-8 flex flex-col gap-2">
            <LegendRow label="Hotel operativi" color="white" />
            <LegendRow label="Nuove aperture" color="#C48F56" />
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative aspect-[4/3] rounded-2xl border border-white/10 bg-[#0A0A0D] overflow-hidden">
            {/* subtle grid */}
            <svg
              className="absolute inset-0 w-full h-full opacity-[0.18]"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <defs>
                <pattern
                  id="grid"
                  width="6"
                  height="6"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 6 0 L 0 0 0 6"
                    fill="none"
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth="0.15"
                  />
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#grid)" />
            </svg>

            {/* MILANO silhouette — road network + landmarks (fedele ma stilizzata) */}
            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="xMidYMid meet"
              aria-hidden="true"
            >
              <defs>
                <radialGradient id="centerGlow" cx="46%" cy="58%" r="55%">
                  <stop offset="0%" stopColor="rgba(196,143,86,0.08)" />
                  <stop offset="60%" stopColor="rgba(196,143,86,0.02)" />
                  <stop offset="100%" stopColor="rgba(0,0,0,0)" />
                </radialGradient>
              </defs>

              {/* ambient warm glow centered on Duomo */}
              <rect width="100" height="100" fill="url(#centerGlow)" />

              {/* Cerchie concentriche: Navigli, Bastioni, Circonvallazione */}
              <circle cx="46" cy="58" r="8" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="0.25" />
              <circle cx="46" cy="58" r="16" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="0.2" strokeDasharray="0.6 0.6" />
              <circle cx="46" cy="58" r="28" fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="0.18" strokeDasharray="1 1" />
              <circle cx="46" cy="58" r="42" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.15" strokeDasharray="1.2 1.4" />

              {/* Raggi — principali direttrici radiali da Duomo verso i quartieri */}
              <g stroke="rgba(255,255,255,0.07)" strokeWidth="0.22" strokeLinecap="round">
                {/* Corso Buenos Aires → Loreto → Bicocca */}
                <path d="M46 58 L62 42 L72 20" />
                {/* Via Vittor Pisani → Centrale → Bignami */}
                <path d="M46 58 L52 38 L48 16" />
                {/* Porta Venezia → Porta Monforte */}
                <path d="M46 58 L60 52" />
                {/* Corso Venezia → Cinque Giornate */}
                <path d="M46 58 L56 64" />
                {/* Corso di Porta Romana → sud */}
                <path d="M46 58 L44 78" />
                {/* Corso Genova → Navigli */}
                <path d="M46 58 L32 70" />
                {/* Magenta / Sempione → NW → Rho */}
                <path d="M46 58 L30 48 L12 50" />
                {/* Via Farini → Maciachini / Monumentale */}
                <path d="M46 58 L38 42 L42 26" />
                {/* Via Melchiorre Gioia / Repubblica */}
                <path d="M46 58 L48 46 L50 34" />
              </g>

              {/* Parco Sempione — blob stilizzato NW del Duomo */}
              <path
                d="M30 48 C28 46 26 44 28 41 C30 38 34 37 38 39 C41 41 41 45 39 47 C37 50 33 51 30 48 Z"
                fill="rgba(196,143,86,0.05)"
                stroke="rgba(196,143,86,0.18)"
                strokeWidth="0.18"
              />

              {/* Binari ferroviari → verso Centrale */}
              <g stroke="rgba(255,255,255,0.12)" strokeWidth="0.18" strokeDasharray="0.5 0.5">
                <path d="M52 38 L56 30 L60 22" />
                <path d="M52 38 L44 30 L38 20" />
              </g>

              {/* Rettangolo Stazione Centrale */}
              <rect
                x="50.5"
                y="36"
                width="3"
                height="4"
                fill="rgba(255,255,255,0.08)"
                stroke="rgba(255,255,255,0.25)"
                strokeWidth="0.15"
              />

              {/* Quadrato Duomo */}
              <rect
                x="44.8"
                y="56.8"
                width="2.4"
                height="2.4"
                fill="rgba(196,143,86,0.25)"
                stroke="rgba(196,143,86,0.6)"
                strokeWidth="0.18"
              />

              {/* N indicator */}
              <g transform="translate(92 8)">
                <circle r="3" fill="rgba(0,0,0,0.4)" stroke="rgba(255,255,255,0.3)" strokeWidth="0.2" />
                <path d="M0 -1.4 L0 1.4 M-0.7 0.4 L0 1.4 L0.7 0.4" stroke="rgba(255,255,255,0.7)" strokeWidth="0.35" fill="none" strokeLinecap="round" />
                <text y="-3.6" textAnchor="middle" fontSize="1.4" fill="rgba(255,255,255,0.5)" fontFamily="ui-monospace, monospace">N</text>
              </g>
            </svg>

            {/* center ring - duomo pulse */}
            <div className="absolute left-[46%] top-[58%] -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none">
              <div className="relative h-2 w-2 rounded-full bg-[#C48F56]" />
              <div className="absolute inset-0 h-2 w-2 rounded-full border border-[#C48F56]/40 animate-ping" />
            </div>

            {POINTS.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className="absolute group z-20"
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
              >
                <div className="relative -translate-x-1/2 -translate-y-1/2">
                  <span
                    className={`absolute inset-0 rounded-full ${
                      p.active ? "bg-white/80" : "bg-[#C48F56]"
                    } animate-ping opacity-40`}
                    style={{ width: 10, height: 10 }}
                  />
                  <span
                    className={`block h-2.5 w-2.5 rounded-full ${
                      p.active ? "bg-white" : "bg-[#C48F56]"
                    } shadow-[0_0_0_3px_rgba(255,255,255,0.08)]`}
                  />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[10px] font-mono uppercase tracking-[0.15em] text-white/50 group-hover:text-white whitespace-nowrap">
                    {p.name}
                  </span>
                </div>
              </motion.div>
            ))}

            <div className="absolute bottom-5 left-5 glass rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.25em] z-30">
              Milano · Visualizzazione portfolio
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LegendRow({ label, color }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="h-2.5 w-2.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="text-sm text-white/60">{label}</span>
    </div>
  );
}
